import { v5, v7, validate } from 'uuid';

/**
 * Microsoft-style UUIDs reach us with a variant nibble the `uuid` package rejects,
 * so the canonical test is `validate` OR this shape.
 */
const reMsUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The platform's single definition of "is this a UUID". Everything that reads an id
 * — decoders via {@link iotsUtils.withPrefix}, and anything parsing ids out of a URL
 * — must go through here, so a format change is one edit rather than a hunt for
 * hand-rolled patterns.
 */
export function isUUID(value: string) {
	return validate(value.toLowerCase()) || reMsUuid.test(value);
}

/**
 * Read a `prefix:uuid` id, the format {@link randomUUID} produces. Returns null when
 * `value` is not one — the uuid half must actually be a uuid, so an arbitrary
 * colon-bearing string does not parse.
 *
 * The prefix is returned as-is rather than checked against a list of known entity
 * kinds: the prefix in the id *is* the kind, and a hardcoded list silently
 * misclassifies every kind added after it was written.
 */
export function parsePrefixedId(value: string) {
	const separator = value.indexOf(':');

	if (separator <= 0) {
		return null;
	}

	const prefix = value.slice(0, separator);
	const uuid = value.slice(separator + 1);

	return isUUID(uuid) ? { prefix, uuid } : null;
}

/**
 * When a UUIDv7 was minted: its first 48 bits are the Unix time in milliseconds. Takes a
 * bare uuid or a `prefix:uuid` id, the format {@link randomUUID} produces.
 *
 * Returns null for anything that is not a v7 uuid. Ids minted before the platform moved
 * to v7 (and v5 {@link predictableUUID}s) carry no time, and must not be read as one.
 */
export function uuidV7Timestamp(value: string): Date | null {
	const uuid = parsePrefixedId(value)?.uuid ?? value;

	if (!isUUID(uuid) || uuid.charAt(14) !== '7') {
		return null;
	}

	return new Date(parseInt(uuid.replace(/-/g, '').slice(0, 12), 16));
}

export function randomUUID(prefix?: string) {
	return prefix ? `${prefix}:${v7()}` : v7();
}

/**
 * A UUID derived from `name`: the same name under the same namespace always
 * produces the same UUID (RFC 9562 v5).
 *
 * Use this — not a raw digest — whenever an id has to be predictable. A digest
 * is not a UUID, and every prefixed id in the platform is decoded through
 * {@link iotsUtils.withPrefix}, which rejects anything after the `:` that is not
 * a valid UUID.
 *
 * @param name the value the id is derived from; equal names give equal UUIDs
 * @param namespace a fixed UUID, so two unrelated domains deriving from the same
 *   name never land on the same id. Never change it once ids have been
 *   persisted — every previously derived id would stop being re-derivable.
 * @param prefix optional entity prefix, prepended as `${prefix}:${uuid}`
 */
export function predictableUUID(name: string, namespace: string, prefix?: string) {
	const uuid = v5(name, namespace);

	return prefix ? `${prefix}:${uuid}` : uuid;
}
